import { isChunkLoadFailure } from '@jepto/shared/services/jep-error-handler/chunk-load-error.util';

import { getGraphQLStyleErrorMessage } from './graphql-style-error-message.util';

/** Substrings matched against GraphQL error text to drop expected auth noise from Sentry. */
export const SENTRY_DROP_GQL_AUTH_SUBSTRINGS = [
	'no current user',
	'user is empty',
	'user is not authenticated',
	'not authenticated',
	'unauthorized',
	'unauthenticated',
] as const;

/** Script URL patterns blocked at Sentry.init (GTM / Tag Assistant third-party noise). */
export const SENTRY_DENY_URLS: ReadonlyArray<string | RegExp> = [
	/gtm-msr\.appspot\.com/i,
	/googletagmanager\.com/i,
	/tagassistant\.google\.com/i,
];

/** Stack frame URL substrings for Google Tag Manager scripts and preview hosts. */
export const SENTRY_GTM_SCRIPT_URL_SUBSTRINGS = [
	'gtm.js',
	'googletagmanager.com',
	'gtm-msr.appspot.com',
	'tagassistant.google.com',
] as const;

/** Page hosts for GTM Tag Assistant / MSR preview events (JEPTO-G). */
export const SENTRY_GTM_PREVIEW_HOST_SUBSTRINGS = [
	'gtm-msr.appspot.com',
	'tagassistant.google.com',
] as const;

/**
 * Path segment of the Ortto Talk chat widget scripts (`/talk-widget.js`,
 * `/talk-widget/master/vN/dist/*.js`), served from Ortto's CDN / `l.jepto.com`.
 */
export const SENTRY_ORTTO_TALK_WIDGET_PATH = '/talk-widget';

/** Filenames the browser reports for native frames: Chrome `<anonymous>`, Safari `[native code]`. */
const NATIVE_FRAME_FILENAMES: ReadonlySet<string> = new Set([
	'<anonymous>',
	'[native code]',
]);

/** Tag set on Ortto Talk widget errors so the Slack alert rule can exclude them (JEP-10665). */
export const SENTRY_ORTTO_TALK_WIDGET_TAG = {
	key: 'third_party',
	value: 'ortto-talk',
} as const;

/** One fingerprint for every Ortto Talk widget crash, so they stay in a single issue. */
export const SENTRY_ORTTO_TALK_WIDGET_FINGERPRINT = [
	'ortto-talk-widget',
] as const;

/** Tag for unmapped `.click()` TypeErrors (JEP-10673) so production alerts can skip them. */
export const SENTRY_UNMAPPED_CLICK_TYPEERROR_TAG = {
	key: 'third_party',
	value: 'unmapped-click',
} as const;

/** One fingerprint for every unmapped click TypeError (JEPTO-287 shape). */
export const SENTRY_UNMAPPED_CLICK_TYPEERROR_FINGERPRINT = [
	'unmapped-click-typeerror',
] as const;

/**
 * Tag for scheduler-only NG0101 so Slack alert rules can exclude stuck-tick noise.
 */
export const SENTRY_ANGULAR_SCHEDULER_NG0101_TAG = {
	key: 'third_party',
	value: 'angular-scheduler-ng0101',
} as const;

export const SENTRY_ANGULAR_SCHEDULER_NG0101_FINGERPRINT = [
	'angular-scheduler-ng0101',
] as const;

/** Jepto app hosts — any script URL on these origins is first-party. */
const JEPTO_APP_HOST_SUBSTRINGS = [
	'app.jepto.com',
	'dev.jepto.com',
	'stg.jepto.com',
	'localhost:',
] as const;

/**
 * Angular entry bundles — hashed in production (`main-abc123.js`) and
 * unhashed in dev (`main.js`). Relative stack frames omit the host.
 */
const JEPTO_APP_BUNDLE_FILENAME_PATTERN =
	/\/(main|vendor|runtime|polyfills|scripts)(-[a-z0-9]+)?\.js/i;

const GTM_BROWSER_TRACING_INTEGRATION_MESSAGE =
	'sentry.browsertracingintegration is not a function';

/** Substrings matched against Error.message for expected unauthenticated noise (Amplify/Cognito). */
export const SENTRY_DROP_AUTH_MESSAGE_SUBSTRINGS = [
	'no current user',
	'user is empty',
	'user is not authenticated',
	'novalidauthtokens',
	'no federated jwt',
] as const;

export interface SentryExceptionFrameLike {
	filename?: string;
	function?: string;
}

export interface SentryExceptionMechanismLike {
	type?: string;
	handled?: boolean;
	data?: Record<string, unknown>;
}

export interface SentryExceptionValueLike {
	type?: string;
	value?: string;
	mechanism?: SentryExceptionMechanismLike;
	stacktrace?: {
		frames?: SentryExceptionFrameLike[];
	};
}

export interface SentryEventLike {
	request?: {
		url?: string;
	};
	exception?: {
		values?: SentryExceptionValueLike[];
	};
	fingerprint?: string[];
	tags?: Record<string, unknown>;
}

export interface SentryHintLike {
	originalException?: unknown;
}

function lowerIncludesAny(
	lower: string,
	substrings: readonly string[]
): boolean {
	return substrings.some(s => lower.includes(s));
}

function collectExceptionTexts(
	event: SentryEventLike,
	original: unknown
): string[] {
	const texts: string[] = [];
	const eventValue = event.exception?.values?.[0]?.value;
	if (typeof eventValue === 'string' && eventValue.length > 0) {
		texts.push(eventValue);
	}
	if (original instanceof Error) {
		texts.push(original.message, original.name);
	} else if (original != null && typeof original === 'object') {
		const gqlMessage = getGraphQLStyleErrorMessage(original);
		if (gqlMessage) {
			texts.push(gqlMessage);
		}
		try {
			texts.push(JSON.stringify(original));
		} catch {
			// ignore
		}
	}
	return texts;
}

function isDuplicatedOperationNoise(
	event: SentryEventLike,
	original: unknown
): boolean {
	return collectExceptionTexts(event, original).some(text =>
		text.includes('DuplicatedOperationError')
	);
}

/** Amplify AppSync websocket drops often surface as empty "Connection failed:" noise. */
function isGenericConnectionFailedMessage(text: string): boolean {
	const match = text
		.trim()
		.match(/(?:graphql:\s*)?connection failed:\s*(.*)$/i);
	if (!match) {
		return false;
	}
	return (match[1] ?? '').trim() === '';
}

/** Expected expired-session Connection failed payloads (JEPTO-24Y / JEP-10548). */
function isExpiredTokenConnectionFailedMessage(text: string): boolean {
	const match = text
		.trim()
		.match(/(?:graphql:\s*)?connection failed:\s*(.*)$/i);
	if (!match) {
		return false;
	}
	const detail = (match[1] ?? '').trim().toLowerCase();
	if (!detail) {
		return false;
	}
	// Rejected realtime handshake: Amplify keeps only the errorType.
	if (detail === 'unauthorizedexception') {
		return true;
	}
	return (
		detail.includes('token has expired') ||
		detail.includes('token expired') ||
		(detail.includes('unauthorizedexception') && detail.includes('expired'))
	);
}

function isAppSyncConnectionFailedNoise(
	event: SentryEventLike,
	original: unknown
): boolean {
	return collectExceptionTexts(event, original).some(
		text =>
			isGenericConnectionFailedMessage(text) ||
			isExpiredTokenConnectionFailedMessage(text)
	);
}

function isUnauthenticatedNoise(
	original: unknown,
	gqlMessage: string | null
): boolean {
	if (gqlMessage) {
		const gqlLower = gqlMessage.toLowerCase();
		if (lowerIncludesAny(gqlLower, SENTRY_DROP_GQL_AUTH_SUBSTRINGS)) {
			return true;
		}
	}
	if (original && typeof original === 'object') {
		const name = (original as { name?: string }).name ?? '';
		const message = String(
			(original as { message?: string }).message ?? ''
		).toLowerCase();
		if (
			name === 'NotAuthenticatedError' ||
			name === 'UserUnAuthenticatedException' ||
			name === 'NoSessionFoundException' ||
			lowerIncludesAny(message, SENTRY_DROP_AUTH_MESSAGE_SUBSTRINGS)
		) {
			return true;
		}
	}
	return false;
}

function getExceptionFrames(
	event: SentryEventLike
): SentryExceptionFrameLike[] {
	return event.exception?.values?.[0]?.stacktrace?.frames ?? [];
}

function isGtmPreviewHostUrl(url: string): boolean {
	const lower = url.toLowerCase();
	return SENTRY_GTM_PREVIEW_HOST_SUBSTRINGS.some(host =>
		lower.includes(host)
	);
}

function isGtmStackFrame(filename: string): boolean {
	const lower = filename.toLowerCase();
	return SENTRY_GTM_SCRIPT_URL_SUBSTRINGS.some(substring =>
		lower.includes(substring)
	);
}

function isJeptoAppStackFrame(filename: string): boolean {
	if (!filename) {
		return false;
	}
	const lower = filename.toLowerCase();
	if (JEPTO_APP_HOST_SUBSTRINGS.some(host => lower.includes(host))) {
		return true;
	}
	if (lower.includes('/chunk-')) {
		return true;
	}
	return JEPTO_APP_BUNDLE_FILENAME_PATTERN.test(lower);
}

function hasJeptoAppStackFrame(event: SentryEventLike): boolean {
	return getExceptionFrames(event).some(frame =>
		isJeptoAppStackFrame(frame.filename ?? '')
	);
}

function hasGtmStackFrame(event: SentryEventLike): boolean {
	return getExceptionFrames(event).some(frame =>
		isGtmStackFrame(frame.filename ?? '')
	);
}

function isGtmBrowserTracingIntegrationNoise(event: SentryEventLike): boolean {
	const message = event.exception?.values?.[0]?.value?.toLowerCase() ?? '';
	if (message !== GTM_BROWSER_TRACING_INTEGRATION_MESSAGE) {
		return false;
	}
	return !hasJeptoAppStackFrame(event);
}

function isGtmOriginatedStacktrace(event: SentryEventLike): boolean {
	const frames = getExceptionFrames(event);
	if (frames.length === 0 || hasJeptoAppStackFrame(event)) {
		return false;
	}
	return hasGtmStackFrame(event);
}

function isGtmThirdPartyNoise(event: SentryEventLike): boolean {
	const requestUrl = event.request?.url ?? '';
	if (requestUrl && isGtmPreviewHostUrl(requestUrl)) {
		return true;
	}
	if (isGtmBrowserTracingIntegrationNoise(event)) {
		return true;
	}
	return isGtmOriginatedStacktrace(event);
}

/**
 * True when every frame with a filename is Ortto Talk widget code. Matches on
 * the `/talk-widget` path rather than {@link isJeptoAppStackFrame}, because
 * the widget's `/dist/vendor.js` also matches the first-party bundle pattern.
 * Native frames (`Array.map` etc.) are skipped: they sit between widget frames
 * in real stacks (JEPTO-1T8) and say nothing about who owns the code. A single
 * Jepto frame (e.g. our own `ap3c.talk.execute` caller) means the error is
 * ours, so it is left untouched.
 */
function isOrttoTalkWidgetError(event: SentryEventLike): boolean {
	const filenames = getExceptionFrames(event)
		.map(frame => frame.filename ?? '')
		.filter(
			filename =>
				filename.length > 0 && !NATIVE_FRAME_FILENAMES.has(filename)
		);
	return (
		filenames.length > 0 &&
		filenames.every(filename =>
			filename.toLowerCase().includes(SENTRY_ORTTO_TALK_WIDGET_PATH)
		)
	);
}

/**
 * Unhandled `.click()` on undefined with no mapped Jepto frames (JEP-10673 /
 * JEPTO-287). Typical of injected / vendor minified code (`J.tab`). Tagged and
 * fingerprinted (not dropped) when a first-party bundle frame is absent.
 */
const UNMAPPED_CLICK_TYPEERROR_PATTERN =
	/cannot read propert(?:y|ies) of undefined \(reading 'click'\)|cannot read property 'click' of undefined/i;

const NG0101_RECURSIVE_TICK_MESSAGE_PATTERN =
	/NG0101|ApplicationRef\.tick is called recursively/i;

/** Stack filenames Sentry reports for zoneless scheduler ticks (prod + Vite dev). */
const ANGULAR_SCHEDULER_STACK_FILENAME_PATTERN =
	/@angular\/core|_debug_node-chunk|_pending_tasks-chunk|vite\/deps\/core-|vite\/deps\/_resource-chunk/i;

/** Function names on scheduler-only NG0101 (JEPTO-290 / bundled main.js stacks). */
const ANGULAR_SCHEDULER_STACK_FUNCTION_PATTERN =
	/ApplicationRef|ChangeDetectionScheduler|NoopNgZone|scheduleCallbackWithRafRace|animationFrameId|tickImpl|\._tick\b/i;

/** App code that legitimately triggers NG0101 — keep alerting when these appear. */
const JEPTO_APP_NG0101_BLAME_FUNCTION_PATTERN =
	/refreshPageData|beginCardsRefresh|KpisPage|ClientsReportComponent|AppComponent#|markForCheckOnEmit|detectChangesInternal|detectChanges\b/i;

const JEPTO_APP_NG0101_BLAME_FILENAME_PATTERN =
	/projects\/jepto\/src|features\/|\.page\.|\.component\./i;

function isAngularSchedulerStackFrame(
	frame: SentryExceptionFrameLike
): boolean {
	const filename = frame.filename ?? '';
	const fn = frame.function ?? '';
	if (ANGULAR_SCHEDULER_STACK_FILENAME_PATTERN.test(filename)) {
		return true;
	}
	return ANGULAR_SCHEDULER_STACK_FUNCTION_PATTERN.test(fn);
}

function isJeptoAppNg0101BlameFrame(frame: SentryExceptionFrameLike): boolean {
	const filename = frame.filename ?? '';
	const fn = frame.function ?? '';
	if (JEPTO_APP_NG0101_BLAME_FILENAME_PATTERN.test(filename)) {
		return true;
	}
	return JEPTO_APP_NG0101_BLAME_FUNCTION_PATTERN.test(fn);
}

/**
 * Frame shape `beforeSend` sees in production: hashed Jepto bundles with
 * minified names, before Sentry source-maps to `@angular/core`.
 */
function isAcceptableNg0101NoiseFrame(
	frame: SentryExceptionFrameLike
): boolean {
	if (isAngularSchedulerStackFrame(frame)) {
		return true;
	}
	return isJeptoAppStackFrame(frame.filename ?? '');
}

/**
 * Scheduler-only NG0101: substantive frames are Angular scheduler code and/or
 * first-party bundles only, with no app blame (manual tick / refresh during CD).
 */
function isAngularSchedulerNg0101Noise(
	event: SentryEventLike,
	original: unknown
): boolean {
	if (
		!collectExceptionTexts(event, original).some(text =>
			NG0101_RECURSIVE_TICK_MESSAGE_PATTERN.test(text)
		)
	) {
		return false;
	}
	const frames = getExceptionFrames(event).filter(
		frame =>
			(frame.filename ?? '').length > 0 &&
			!NATIVE_FRAME_FILENAMES.has(frame.filename ?? '')
	);
	if (frames.length === 0) {
		return false;
	}
	if (frames.some(isJeptoAppNg0101BlameFrame)) {
		return false;
	}
	return frames.every(isAcceptableNg0101NoiseFrame);
}

function isUnmappedClickTypeError(
	event: SentryEventLike,
	original: unknown
): boolean {
	const frames = getExceptionFrames(event);
	if (frames.length === 0 || hasJeptoAppStackFrame(event)) {
		return false;
	}
	return collectExceptionTexts(event, original).some(text =>
		UNMAPPED_CLICK_TYPEERROR_PATTERN.test(text)
	);
}

function isChunkLoadSentryNoise(
	event: SentryEventLike,
	original: unknown
): boolean {
	if (isChunkLoadFailure(original)) {
		return true;
	}

	return collectExceptionTexts(event, original).some(text =>
		isChunkLoadFailure(text)
	);
}

/**
 * Angular service worker install failures on a local origin (e.g. a production
 * build served from localhost:4200). Local servers rewrite assets, so ngsw.json
 * hashes never match; the event is tagged "production" but is not a real user.
 */
const LOCAL_SW_HASH_MISMATCH_PATTERN =
	/^hash mismatch \([^)]*\):\s*https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?\//i;

/** Sentry Replay compression worker `ensureReady` on a null worker message (JEPTO-291). */
export const REPLAY_WORKER_NULL_SUCCESS_PATTERN =
	/cannot read propert(?:y|ies) of null \(reading 'success'\)/i;

function matchesReplayWorkerNullSuccessMessage(
	event: SentryEventLike,
	original: unknown
): boolean {
	return collectExceptionTexts(event, original).some(text =>
		REPLAY_WORKER_NULL_SUCCESS_PATTERN.test(text)
	);
}

function isSentryReplayWorkerAddEventListenerMechanism(
	value: SentryExceptionValueLike | undefined
): boolean {
	const type = value?.mechanism?.type?.toLowerCase() ?? '';
	if (
		!type.includes('browserapierrors') ||
		!type.includes('addeventlistener')
	) {
		return false;
	}
	const target = value?.mechanism?.data?.target;
	return typeof target === 'string' && target.toLowerCase() === 'worker';
}

function isSentryReplayWorkerGlobalOnErrorMechanism(
	value: SentryExceptionValueLike | undefined
): boolean {
	return (
		value?.mechanism?.type?.toLowerCase() ===
		'auto.browser.global_handlers.onerror'
	);
}

/** `beforeSend` sees hashed bundle URLs, not Sentry's source-mapped SDK paths. */
function framesAreNativeOrJeptoBundleOnly(event: SentryEventLike): boolean {
	const frames = getExceptionFrames(event);
	if (frames.length === 0) {
		return false;
	}
	return frames.every(frame => {
		const filename = frame.filename ?? '';
		if (filename.length === 0 || NATIVE_FRAME_FILENAMES.has(filename)) {
			return true;
		}
		if (isGtmStackFrame(filename)) {
			return false;
		}
		return isJeptoAppStackFrame(filename);
	});
}

function isSentryReplayWorkerNullSuccessNoise(
	event: SentryEventLike,
	original: unknown
): boolean {
	if (!matchesReplayWorkerNullSuccessMessage(event, original)) {
		return false;
	}
	const value = event.exception?.values?.[0];
	if (isSentryReplayWorkerAddEventListenerMechanism(value)) {
		return true;
	}
	return (
		isSentryReplayWorkerGlobalOnErrorMechanism(value) &&
		framesAreNativeOrJeptoBundleOnly(event)
	);
}

function isLocalServiceWorkerHashMismatchNoise(
	event: SentryEventLike,
	original: unknown
): boolean {
	return collectExceptionTexts(event, original).some(text =>
		LOCAL_SW_HASH_MISMATCH_PATTERN.test(text.trim())
	);
}

/**
 * Kept after the zoneless migration (JEP-10512): tabs still running a cached
 * pre-zoneless bundle keep reporting zone-wrapped frames for a while.
 */
function isZoneWrappedException(event: SentryEventLike): boolean {
	const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
	return frames.some(frame => {
		const filename = frame.filename ?? '';
		const fn = frame.function ?? '';
		return filename.includes('zone.js') || fn.includes('ZoneImpl');
	});
}

function stabilizeFingerprintValue(value: string): string {
	return value.replace(
		/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
		'<id>'
	);
}

/**
 * Sentry beforeSend: drop expected Amplify/auth noise, rewrite GraphQL payloads,
 * tag unmapped `.click()` TypeErrors with no Jepto frames (JEPTO-287),
 * tag and group Ortto Talk widget crashes, and fingerprint Zone-wrapped errors
 * so they do not all group as ZoneImpl.runTask.
 */
export function applySentryBeforeSend<T extends SentryEventLike>(
	event: T,
	hint?: SentryHintLike
): T | null {
	const original = hint?.originalException;
	const gqlMessage = getGraphQLStyleErrorMessage(original);
	if (gqlMessage && event.exception?.values?.[0]) {
		event.exception.values[0].value = gqlMessage;
		event.exception.values[0].type = 'GraphQL';
	}

	if (isDuplicatedOperationNoise(event, original)) {
		return null;
	}

	if (isAppSyncConnectionFailedNoise(event, original)) {
		return null;
	}

	if (isUnauthenticatedNoise(original, gqlMessage)) {
		return null;
	}

	if (isGtmThirdPartyNoise(event)) {
		return null;
	}

	if (isChunkLoadSentryNoise(event, original)) {
		return null;
	}

	if (isLocalServiceWorkerHashMismatchNoise(event, original)) {
		return null;
	}

	if (isSentryReplayWorkerNullSuccessNoise(event, original)) {
		return null;
	}

	// Kept (not dropped): these crash the chat launcher until reload, and the
	// event count is our evidence for Ortto. Tagged so the alert rule skips them.
	if (isOrttoTalkWidgetError(event)) {
		event.fingerprint = [...SENTRY_ORTTO_TALK_WIDGET_FINGERPRINT];
		event.tags = {
			...event.tags,
			[SENTRY_ORTTO_TALK_WIDGET_TAG.key]:
				SENTRY_ORTTO_TALK_WIDGET_TAG.value,
		};
		return event;
	}

	if (isUnmappedClickTypeError(event, original)) {
		event.fingerprint = [...SENTRY_UNMAPPED_CLICK_TYPEERROR_FINGERPRINT];
		event.tags = {
			...event.tags,
			[SENTRY_UNMAPPED_CLICK_TYPEERROR_TAG.key]:
				SENTRY_UNMAPPED_CLICK_TYPEERROR_TAG.value,
		};
		return event;
	}

	if (isAngularSchedulerNg0101Noise(event, original)) {
		event.fingerprint = [...SENTRY_ANGULAR_SCHEDULER_NG0101_FINGERPRINT];
		event.tags = {
			...event.tags,
			[SENTRY_ANGULAR_SCHEDULER_NG0101_TAG.key]:
				SENTRY_ANGULAR_SCHEDULER_NG0101_TAG.value,
		};
		return event;
	}

	if (isZoneWrappedException(event) && event.exception?.values?.[0]) {
		const first = event.exception.values[0];
		const type = first.type || 'Error';
		const value = stabilizeFingerprintValue(first.value || '');
		event.fingerprint = ['zone-unhandled', type, value];
	}

	return event;
}
